/**
 * @see https://github.com/callstack/react-native-paper/tree/master/src/components/TouchableRipple
 */
import * as React from 'react';
import {
  BackgroundPropType,
  StyleProp,
  Platform,
  TouchableNativeFeedback,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
  TouchableOpacity,
} from 'react-native';

const ANDROID_VERSION_LOLLIPOP = 21;
const ANDROID_VERSION_PIE = 28;

type Props = React.ComponentProps<typeof TouchableWithoutFeedback> & {
  borderless?: boolean;
  background?: BackgroundPropType;
  disabled?: boolean;
  onPress?: () => void | null;
  rippleColor?: string;
  underlayColor?: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

export const Tappable = ({
  style,
  background,
  borderless = false,
  disabled,
  rippleColor,
  children,
  ...rest
}: Props) => {
  const calculatedRippleColor = rippleColor || 'rgba(0, 0, 0, .2)';

  // A workaround for ripple on Android P is to use useForeground + overflow: 'hidden'
  // https://github.com/facebook/react-native/issues/6480
  const useForeground =
    Platform.OS === 'android' && Platform.Version >= ANDROID_VERSION_PIE && borderless;

  if (Tappable.supported) {
    return (
      <TouchableNativeFeedback
        {...rest}
        disabled={disabled}
        useForeground={useForeground}
        background={
          background != null
            ? background
            : TouchableNativeFeedback.Ripple(calculatedRippleColor, borderless)
        }
      >
        <View style={[borderless && { overflow: 'hidden' }, style]}>
          {React.Children.only(children)}
        </View>
      </TouchableNativeFeedback>
    );
  }

  return (
    <TouchableOpacity
      {...rest}
      disabled={disabled}
      style={[borderless && { overflow: 'hidden' }, style]}
      activeOpacity={0.6}
    >
      {React.Children.only(children)}
    </TouchableOpacity>
  );
};

Tappable.supported = Platform.OS === 'android' && Platform.Version >= ANDROID_VERSION_LOLLIPOP;
